import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getOnboardingStatus } from '@/lib/onboarding-status';

/**
 * Route guard that ensures the participant has completed all onboarding steps:
 * 1. Profile complete (gender, age_range, accent_label, preferred_environment)
 * 2. IDV verified
 * 3. Consent accepted (current version)
 *
 * Redirects to the appropriate step when a gate fails.
 * Requirements 3.1: Block access to recording tasks and redirect if onboarding incomplete.
 */
export function ProfileGate() {
  const [status, setStatus] = useState<'loading' | 'complete' | 'incomplete'>('loading');
  const [redirectPath, setRedirectPath] = useState('/profile');
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const checkOnboarding = async () => {
      const onboardingStatus = await getOnboardingStatus();
      if (!cancelled) {
        if (onboardingStatus.isComplete) {
          setStatus('complete');
        } else {
          setRedirectPath(onboardingStatus.redirectPath);
          setStatus('incomplete');
        }
      }
    };

    checkOnboarding();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (status === 'incomplete') {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  return <Outlet />;
}
